import { ApplicationConfig, APP_INITIALIZER, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideEffects } from '@ngrx/effects';

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import { AuthInterceptor } from './services/AuthInterceptor';
import { LoadingInterceptor } from './services/LoadingInterceptor';
import { CorrelationInterceptor } from './services/correlation.interceptor';
import OrderService from './services/OrderService';
import { OrderEffects } from './state/order/order.effects';
import { orderReducer } from './state/order/order.reducer';
import { CustomerEffects } from './state/customer/customer.effects';
import { customerReducer } from './state/customer/customer.reducer';
import { commentReducer } from './state/comment/comment.reducer';
import CustomerService from './services/CustomerService';
import { CommentService } from './services/CommentService';

// MSAL imports
import { RoleService } from './services/RoleService';
import { NavigationService } from './services/NavigationService';
import { faqReducer } from './state/faq/faq.reducer';
import { FaqEffects } from './state/faq/faq.effects';
import { FaqService } from './services/FaqService';
import { FeedbackEffects } from './state/feedback/feedback.effects';
import { FeedbackService } from './services/FeedbackService';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { CommentEffects } from './state/comment/comment.effects';
import { reasonReducer } from './state/reason/reason.reducer';
import { feedbackReducer } from './state/feedback/feedback.reducer';
import { ReasonEffects } from './state/reason/reason.effects';
import ReasonService from './services/ReasonService';
import CoinTransactionService from './services/CoinTransactionService';
import { CoinTransactionEffects } from './state/coin-transaction/coin-transaction.effects';
import { coinTransactionReducer } from './state/coin-transaction/coin-transaction.reducer';
import { BffService } from './services/BffService';
import { environment } from '../environments/environment';
import { DualAuthService } from './services/DualAuthService';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { getGermanPaginatorIntl } from '../util/german-paginator-intl';
import { coinTypeReducer } from './state/coin-type/coin-type.reducer';
import { CoinTypeEffects } from './state/coin-type/coin-type.effects';
import { CoinTypeService } from './services/CoinTypeService';
import { notificationReducer } from './state/notification/notification.reducer';
import { NotificationEffects } from './state/notification/notification.effects';
import NotificationService from './services/NotificationService';
import { SignalRService } from './services/SignalRService';
import InvoiceService from './services/InvoiceService';
import { InvoiceEffects } from './state/invoice/invoice.effects';
import { invoiceReducer } from './state/invoice/invoice.reducer';
import { salesUserReducer } from './state/sales-user/sales-user.reducer';
import { SalesUserEffects } from './state/sales-user/sales-user.effects';
import SalesUserService from './services/SalesUserService';
import UserService from './services/UserService';
import { userReducer } from './state/user/user.reducer';
import { UserEffects } from './state/user/user.effects';

// MSAL factory functions
// Removed MSAL; use BFF only
export function initAuthFactory(dual: DualAuthService): () => Promise<void> {
  return () => dual.ensureInitialized();
}

export function initCsrfFactory(bff: BffService): () => Promise<void> {
  return () => (environment.authMode === 'bff' ? bff.ensureCsrf() : Promise.resolve());
}

//export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
//  const protectedResourceMap = new Map<string, Array<string>>();
//  protectedResourceMap.set(
//    protectedResources.api.endpoint,
//    protectedResources.api.scopes
//  );
//
//  return {
//    interactionType: InteractionType.Redirect,
//    protectedResourceMap,
//  };
//}

function browserLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'de-DE';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    // Disable hydration to avoid SSR warnings in development
    // provideClientHydration(withEventReplay()),
    provideStore({
      order: orderReducer,
      customer: customerReducer,
      comment: commentReducer,
      faq: faqReducer,
      reason: reasonReducer,
      feedback: feedbackReducer,
      coinTransaction: coinTransactionReducer,
      coinType: coinTypeReducer,
      notification: notificationReducer,
      invoice: invoiceReducer,
      salesUser: salesUserReducer,
      user: userReducer,
    }),
    provideZoneChangeDetection(),
    provideEffects([
      OrderEffects,
      CustomerEffects,
      FaqEffects,
      CommentEffects,
      FeedbackEffects,
      ReasonEffects,
      CoinTransactionEffects,
      CoinTypeEffects,
      NotificationEffects,
      InvoiceEffects,
      SalesUserEffects,
      UserEffects,
    ]),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),

    // Custom services
    DualAuthService,
    RoleService,
    NavigationService,
    {
      provide: APP_INITIALIZER,
      useFactory: initCsrfFactory,
      deps: [BffService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAuthFactory,
      deps: [DualAuthService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CorrelationInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },

    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 2000,
        verticalPosition: 'bottom',
        horizontalPosition: 'right',
      },
    },
    { provide: MatPaginatorIntl, useValue: getGermanPaginatorIntl() },
    // Existing services
    OrderService,
    CustomerService,
    CommentService,
    FaqService,
    FeedbackService,
    ReasonService,
    CoinTransactionService,
    CoinTypeService,
    NotificationService,
    SignalRService,
    InvoiceService,
    SalesUserService,
    UserService,
    { provide: LOCALE_ID, useFactory: browserLocale },
    { provide: MAT_DATE_LOCALE, useFactory: browserLocale }
  ],
};
